import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { createCognitoCredentials, createS3Client, loadS3ClientModule } from '../../utils/aws-s3.util';

interface SaveResponse {
  result?: 'success';
  error?: string;
  card_key?: string;
  default_image_id?: number;
}

/**
 * Makes the invite card the host picked into the event's link preview, and
 * saves a cover changed from the share screen.
 *
 * Card: the image goes straight to S3 with the same Cognito credentials the
 * branding-logo upload uses (events/{eventId}/... is already writable), then
 * pwa-save-invite-card records the key so generate-preview-url serves it as
 * og:image. Every upload gets a new file name: CloudFront caches by key, so
 * overwriting one file would keep serving the old look.
 *
 * Everything here is inert while `environment.inviteCard.saveUrl` is empty.
 */
@Injectable({ providedIn: 'root' })
export class InviteCardService {
  constructor(private http: HttpClient, private afAuth: AngularFireAuth) {}

  get enabled(): boolean {
    return !!(environment as any).inviteCard?.saveUrl;
  }

  /** Uploads the card and records it. Resolves to the saved S3 key; rejects on any failure. */
  async saveCard(eventId: any, file: File, template: string): Promise<string> {
    if (!this.enabled) throw new Error('invite card saving is off');
    const token = await this.idToken();
    const key = `events/${eventId}/invite/card-${Date.now()}.jpg`;

    const region = environment.aws.region || 'us-west-1';
    const s3 = await createS3Client({
      region,
      credentials: await createCognitoCredentials({
        identityPoolId: environment.aws.IdentityPoolId,
        logins: { 'securetoken.google.com/picaggo-235807': token },
        clientConfig: { region },
      }),
    });
    const { PutObjectCommand } = await loadS3ClientModule();
    await s3.send(new PutObjectCommand({
      Bucket: environment.aws.bucketName,
      Key: key,
      Body: file,
      ContentType: 'image/jpeg',
      // Safe to cache for good: the key changes whenever the card does.
      CacheControl: 'public, max-age=31536000, immutable',
    }));

    const res = await this.post({ action: 'card', event_id: String(eventId), card_key: key, template }, token);
    if (res.result !== 'success') throw new Error(res.error || 'save failed');
    return key;
  }

  /**
   * Saves a new event cover (the base64 JPEG CoverImageComponent returns).
   * This is the event's real cover — the gallery shows it too.
   */
  async saveCover(eventId: any, coverDataUrl: string): Promise<void> {
    if (!this.enabled) throw new Error('invite card saving is off');
    const token = await this.idToken();
    const res = await this.post({ action: 'cover', event_id: String(eventId), cover: coverDataUrl }, token);
    if (res.result !== 'success') throw new Error(res.error || 'save failed');
  }

  private post(body: Record<string, string>, token: string): Promise<SaveResponse> {
    // JSON, not form-encoded: a base64 cover has '+' in it, which form
    // encoding turns into spaces. The Lambda parses JSON for this reason.
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return firstValueFrom(this.http.post<SaveResponse>((environment as any).inviteCard.saveUrl, body, { headers }));
  }

  private async idToken(): Promise<string> {
    const user = await this.afAuth.currentUser;
    if (!user) throw new Error('not signed in');
    return user.getIdToken();
  }
}
